import {
  Box, Button, Grid, TextField, Typography, Select, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow,
  Divider, FormControl, InputLabel,
} from '@mui/material';
import { BasePathProvider, useBasePath } from 'components/Providers'
import { useTranslation } from 'react-i18next'
import Header from 'components/Header'
import { FaSave } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState, Fragment } from 'react';
import { getStandardBasedItems } from 'store/lookup';
import { useForm, Controller } from 'react-hook-form'
import { save, search } from 'store/teacher/standard-based';
import { save as saveFinalGrade, search as searchFinalGrades } from 'store/attendance/final-grades';
import { alpha } from '@mui/material/styles';

const getGradeForMP = (learningObjectiveGrades, mpId) => {
  if (!Array.isArray(learningObjectiveGrades) || !mpId) return '';
  const gradeObj = learningObjectiveGrades.find(g => String(g.markingPeriodId) === String(mpId));
  return gradeObj?.grade !== undefined && gradeObj?.grade !== null ? gradeObj.grade : '';
}

export default function FormIndex({ studentStandardBasedGrades, selectedTerm, selectedCourseSession, selectedStudent, markingPeriods }) {
  const { t } = useTranslation() 
  const basePath = useBasePath()
  const dispatch = useDispatch()
  
  const [grades, setGrades] = useState({}) 
  const [applyAllGrade, setApplyAllGrade] = useState('');
  let currentCategory = null;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      overallGrade: '',
      comment: '',
    }
  })

  const gradeLetters = useSelector((state) => state.lookup?.gradeLetters || []);
  const learningObjectivesLookup = useSelector((state) => state.lookup?.standardBasedItems || []); 

  const finalGradesList = useSelector((state) => state.academic?.finalGrades?.list || []);

  const selectedTermIndex = useMemo(() => {
    if (!selectedTerm || !markingPeriods) return -1;
    return markingPeriods.findIndex(mp => String(mp.id) === String(selectedTerm.id));
  }, [markingPeriods, selectedTerm]);

  const previousTerm = useMemo(() => {
    if (selectedTermIndex > 0) return markingPeriods[selectedTermIndex - 1];
    return null;
  }, [selectedTermIndex, markingPeriods]);

  const uniqueGradeLetters = useMemo(() => {
    if (!gradeLetters) return [];
    const seen = new Set();
    return gradeLetters.filter(g => {
      const duplicate = seen.has(g.letterGrade);
      seen.add(g.letterGrade);
      return !duplicate;
    });
  }, [gradeLetters]);

  useEffect(() => {
    const courseSessionId = selectedCourseSession?.id || selectedCourseSession?.course?.id;
    if (courseSessionId) {
        dispatch(searchFinalGrades({ courseSessionId }));
    }
  }, [dispatch, selectedCourseSession]);

  useEffect(() => {
    const courseId = selectedCourseSession?.courseId || selectedCourseSession?.course?.id;
    if (courseId) {
      dispatch(getStandardBasedItems({ courseId }));
    }
  }, [dispatch, selectedCourseSession]);

  useEffect(() => {
    if (!Array.isArray(studentStandardBasedGrades) || !selectedTerm) return;
    
    const initialGrades = {};
    studentStandardBasedGrades.forEach((standard, index) => {
      const grade = getGradeForMP(standard.learningObjectiveGrades, selectedTerm.id);
      initialGrades[index] = grade; 
    });
    setGrades(initialGrades);
    setApplyAllGrade('');

    reset({
      overallGrade: '',
      comment: '',
    });
  }, [studentStandardBasedGrades, selectedTerm, reset]);

  const handleGradeChange = (index, value) => {
    setGrades(prev => ({
      ...prev,
      [index]: value, 
    }));
  };

  const handleApplyAllChange = (value) => {
    setApplyAllGrade(value);
    if (Array.isArray(studentStandardBasedGrades)) {
      const newGrades = {};
      studentStandardBasedGrades.forEach((standard, index) => {
        newGrades[index] = value;
      });
      setGrades(newGrades);
    }
  };

  const handleCopyPreviousGrades = () => {
    if (!previousTerm || !Array.isArray(studentStandardBasedGrades)) {
      return;
    }
    const newGrades = {};
    studentStandardBasedGrades.forEach((standard, index) => {
      const prevGrade = getGradeForMP(standard.learningObjectiveGrades, previousTerm.id);
      newGrades[index] = prevGrade !== '' ? prevGrade : '';
    });
    setGrades(newGrades);
    setApplyAllGrade('');
  };

  const onSubmit = async (data) => {
    const cseId = selectedStudent?.id;

    if (!cseId || !selectedTerm) {
      return
    }

    if (data.overallGrade !== '' && data.overallGrade !== null && data.overallGrade !== undefined) {
      const courseSessionId = selectedCourseSession?.id || selectedCourseSession?.course?.id;
      
      if (courseSessionId) {
        let markingPeriodGradeId = 0;
        if (Array.isArray(finalGradesList)) {
            const studentRecord = finalGradesList.find(r => r.id === cseId);
            if (studentRecord && Array.isArray(studentRecord.markingPeriodGrades)) {
                const existingMPGrade = studentRecord.markingPeriodGrades.find(
                    mp => String(mp.markingPeriodId) === String(selectedTerm.id)
                );
                if (existingMPGrade) {
                    markingPeriodGradeId = existingMPGrade.id;
                }
            }
        }

        const finalGradePayload = {
          courseSessionId, 
          id: cseId,
          markingPeriodGrades: [
            {
              markingPeriodGradeId,
              markingPeriodId: selectedTerm.id,
              grade: Number(data.overallGrade)
            }
          ]
        };

        try {
          await dispatch(saveFinalGrade(finalGradePayload)).unwrap();
          dispatch(searchFinalGrades({ courseSessionId }));
        } catch (error) {
          console.error("Failed to post Final Grade:", error);
        }
      }
    }

    const learningObjectiveGrades = [];
    Object.keys(grades).forEach(index => {
      const gradeValue = grades[index];
      const standardOnScreen = studentStandardBasedGrades[index];
      if (!standardOnScreen || gradeValue === '' || gradeValue === undefined || gradeValue === null) return;

      let realId = null;
      if (Array.isArray(standardOnScreen.learningObjectiveGrades) && standardOnScreen.learningObjectiveGrades.length > 0) {
          const existingRecord = standardOnScreen.learningObjectiveGrades.find(x => x.learningObjectiveId);
          if (existingRecord) realId = existingRecord.learningObjectiveId;
      }
      if (!realId && learningObjectivesLookup && learningObjectivesLookup.length > 0) {
         if (standardOnScreen.code && standardOnScreen.description) {
            const match = learningObjectivesLookup.find(lo => 
               lo.code === standardOnScreen.code && 
               lo.description?.trim() === standardOnScreen.description?.trim()
            );
            if (match) realId = match.id;
         }
         if (!realId && standardOnScreen.description) {
            const match = learningObjectivesLookup.find(lo => 
               lo.description?.trim() === standardOnScreen.description?.trim()
            );
            if (match) realId = match.id;
         }
         if (!realId && standardOnScreen.code) {
            const match = learningObjectivesLookup.find(lo => lo.code === standardOnScreen.code);
            if (match) realId = match.id;
         }
      }
      if (!realId) realId = standardOnScreen.id || standardOnScreen.seq; 

      if (realId) {
        learningObjectiveGrades.push({
            learningObjectiveId: realId, 
            grade: Number(gradeValue), 
        });
      }
    });

    const payload = {
      courseSessionEnrollmentId: cseId,
      markingPeriodId: selectedTerm.id,
      learningObjectiveGrades,
    };

    try {
      await dispatch(save(payload)).unwrap();
      if (cseId) {
        dispatch(search({ courseSessionEnrollmentId: cseId }));
      }
    } catch (error) {
      console.error("Failed to save grades:", error);
    }
  };

  return (
    <BasePathProvider value={basePath}>
      <Box
        data-component="StandardBasedGradeForm"
        component="form"
        noValidate
        sx={{ display: 'flex', flexDirection: 'column', height: '100vh', border: 'none' }}
      >
        <Header title={t('Edit Overall Grade')} small close borderBottom />

        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>

          <Grid container spacing={1} sx={{ px: 2, pb: 2 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', mb: 2, flexWrap: 'wrap' }}>
                <Box sx={{ flex: 1, minWidth: '150px' }}>
                  <Typography variant="caption" color="text.secondary">{t('Student Name')}</Typography>
                  <Typography variant="body1">
                    {selectedStudent?.schoolEnrollment?.person
                      ? `${selectedStudent.schoolEnrollment.person.last_name}, ${selectedStudent.schoolEnrollment.person.first_name}`
                      : (selectedStudent?.person?.last_name ? `${selectedStudent.person.last_name}, ${selectedStudent.person.first_name}` : '-')}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 1, display: { xs: 'none', md: 'block' } }} />
                <Box sx={{ flex: 1, minWidth: '150px' }}>
                  <Typography variant="caption" color="text.secondary">{t('Course Session')}</Typography>
                  <Typography variant="body1" >
                    {selectedCourseSession?.name || '-'}
                  </Typography>
                </Box>
                <Divider orientation="vertical" flexItem sx={{ mx: 1, display: { xs: 'none', md: 'block' } }} />
                <Box sx={{ flex: 1, minWidth: '150px' }}>
                  <Typography variant="caption" color="text.secondary">{t('Marking Period')}</Typography>
                  <Typography variant="body1" >
                    {selectedTerm?.name || '-'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12} sx={{ p: 2, pt: 0 }}>
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                variant="contained"
                startIcon={<FaSave />}
                onClick={handleSubmit(onSubmit)}
                disabled={!selectedTerm}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': { backgroundColor: 'primary.dark' }
                }}
              >
                {t('Save')}
              </Button>
            </Box>
          </Grid>

          <Grid container spacing={2} sx={{ p: 2 }}>
            <Grid item xs={12}>
              <Controller
                name="overallGrade"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                    <InputLabel shrink>{t('Grade')}</InputLabel>
                    <Select
                      {...field}
                      label={t('Grade')}
                      displayEmpty
                    >
                      <MenuItem value=""><em>{t('None')}</em></MenuItem>
                      {uniqueGradeLetters.map(g => (
                          <MenuItem key={g.id} value={g.maxGrade}>
                            {g.letterGrade}
                          </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="comment"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    multiline
                    fullWidth
                    minRows={3}
                    placeholder={t('Enter comment...')}
                    sx={{ mt: 1 }}
                    InputLabelProps={{ shrink: true }}
                    label={t('Comment')}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel shrink>{t('Apply to All')}</InputLabel>
                  <Select
                    value={applyAllGrade}
                    onChange={(e) => handleApplyAllChange(e.target.value)}
                    displayEmpty
                    label={t('Apply to All')}
                    renderValue={(selected) => {
                       if (selected === '') return <em>{t('None')}</em>;
                       const match = gradeLetters.find(gl => String(gl.maxGrade) === String(selected));
                       return match ? match.letterGrade : selected;
                    }}
                  >
                    <MenuItem value=""><em>{t('None')}</em></MenuItem>
                    {uniqueGradeLetters.map(g => (
                        <MenuItem key={g.id} value={g.maxGrade}>
                          {g.letterGrade}
                        </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {previousTerm && (
                  <Button
                    variant="outlined"
                    onClick={handleCopyPreviousGrades}
                    sx={{
                      color: 'primary.main',
                      borderColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        borderColor: 'primary.main'
                      },
                      flexShrink: 0
                    }}
                  >
                    {t('Copy Previous MP Grades')}
                  </Button>
                )}
              </Box>
            </Grid>

          </Grid>

          <Box sx={{ px: 2 }}>
            <Table sx={{ borderCollapse: 'separate', borderSpacing: 0, mt: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '0%', borderBottom: 1, borderColor: 'divider', pl: 2 }}>{t('Learning Standard')}</TableCell>
                  {previousTerm && (
                    <TableCell align="center" sx={{ width: '20%', borderBottom: 1, borderColor: 'divider' }}>
                      {previousTerm.name}
                    </TableCell>
                  )}
                  <TableCell align="center" sx={{ width: '20%', borderBottom: 1, borderColor: 'divider' }}>
                    {selectedTerm ? selectedTerm.name : t('Current MP')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!studentStandardBasedGrades || studentStandardBasedGrades.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={previousTerm ? 3 : 2} align="center" sx={{ borderBottom: 1, borderColor: 'divider', p: 3 }}>
                      {t('No learning standards available')}
                    </TableCell>
                  </TableRow>
                ) : (
                  studentStandardBasedGrades.map((item, index) => {
                    const showCategory = item.category !== currentCategory;
                    currentCategory = item.category;
                    
                    const rowKey = item.seq || item.id || `${item.code}-${index}`;
                    
                    const currentGradeVal = grades[index] !== undefined ? grades[index] : '';
                    
                    const previousGradeVal = getGradeForMP(item.learningObjectiveGrades, previousTerm?.id);
                    
                    return (
                      <Fragment key={rowKey}>
                        {showCategory && (
                          <TableRow>
                            <TableCell colSpan={previousTerm ? 3 : 2} sx={{ fontWeight: '500', color: 'text.primary', borderBottom: 1, borderColor: 'divider' }}>
                              {item.category}
                            </TableCell>
                          </TableRow>
                        )}
                        <TableRow hover>

                          <TableCell sx={{ width: '60%', pl: 2, borderBottom: 1, borderColor: 'divider' }}>
                            <Typography variant="body2">
                              {item.description}
                            </Typography>
                          </TableCell>

                          {previousTerm && (
                            <TableCell align="center" sx={{ width: '20%', borderBottom: 1, borderColor: 'divider' }}>
                              {(() => {
                                if (previousGradeVal === '') return t('N/A');
                                const match = gradeLetters.find(gl => String(gl.maxGrade) === String(previousGradeVal));
                                return match ? match.letterGrade : previousGradeVal;
                              })()}
                            </TableCell>
                          )}

                          <TableCell align="center" sx={{ width: '20%', borderBottom: 1, borderColor: 'divider' }}>
                            <FormControl fullWidth size="small">
                              <InputLabel shrink>{t('Grade')}</InputLabel>
                              <Select
                                label={t('Grade')}
                                value={currentGradeVal}
                                onChange={(e) => handleGradeChange(index, e.target.value)}
                                displayEmpty
                                disabled={!selectedTerm}
                                renderValue={(selected) => {
                                   if (selected === '' || selected === undefined || selected === null) return <em>{t('None')}</em>;
                                   const match = gradeLetters.find(gl => String(gl.maxGrade) === String(selected));
                                   return match ? match.letterGrade : selected;
                                }}
                              >
                                <MenuItem value=""><em>{t('None')}</em></MenuItem>
                                {uniqueGradeLetters.map(g => (
                                  <MenuItem key={g.id} value={g.maxGrade}>
                                    {g.letterGrade}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                      </Fragment>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </Box>

        </Box>
      </Box>
    </BasePathProvider>
  )
}